import type { IAdProvider, AdProviderId, AdStatus, AdProviderConfig } from './types';
import { GigahubProvider } from './providers/GigahubProvider';
import { MonetagProvider } from './providers/MonetagProvider';
import { OnclickaProvider } from './providers/OnclickaProvider';

/** 廣告商配置映射 */
type ProviderConfigMap = {
  gigapub: { projectId: string | number };
  monetag: { projectId: string | number;[key: string]: any };
  onclicka: { projectId: string | number;[key: string]: any };
};

/** 廣告完成事件數據 */
export interface AdCompletedEventData {
  provider: AdProviderId;
  data: any;
  timestamp: number;
}

/** 廣告完成事件處理函數 */
export type AdCompletedHandler = (event: AdCompletedEventData) => void | Promise<void>;

export class AdService {
  private providers: Map<AdProviderId, IAdProvider> = new Map();
  private currentProvider: IAdProvider | null = null;
  private providerOrder: AdProviderId[] = ['gigapub', 'monetag', 'onclicka']; // 優先順序
  private configs: Partial<ProviderConfigMap> = {};
  private completedHandlers: AdCompletedHandler[] = [];

  /**
   * 註冊廣告商配置
   */
  registerProvider<T extends AdProviderId>(
    type: T,
    config: ProviderConfigMap[T]
  ): void {
    this.configs[type] = config;

    let provider: IAdProvider;
    switch (type) {
      case 'gigapub':
        provider = new GigahubProvider(config);
        break;
      case 'monetag':
        provider = new MonetagProvider(config);
        break;
      case 'onclicka':
        provider = new OnclickaProvider(config);
        break;
      default:
        throw new Error(`Unknown ad provider: ${type}`);
    }

    this.providers.set(type, provider);
  }

  /**
   * 初始化所有已註冊的廣告商
   */
  async initAll(): Promise<void> {
    const initPromises = Array.from(this.providers.values()).map(provider =>
      provider.init().catch(error => {
        console.error(`[AdService] Failed to init ${provider.providerId}:`, error);
        return null;
      })
    );

    await Promise.all(initPromises);
  }

  /**
   * 根據優先順序選擇可用的廣告商
   */
  private selectProvider(): IAdProvider | null {
    // 如果當前提供商可用，優先使用
    if (this.currentProvider && this.currentProvider.getStatus() === 'ready') {
      return this.currentProvider;
    }

    // 按優先順序查找第一個可用的提供商
    for (const providerType of this.providerOrder) {
      const provider = this.providers.get(providerType);
      if (provider && provider.getStatus() === 'ready') {
        return provider;
      }
    }

    return null;
  }

  /**
   * 切換到下一個廣告商（輪播機制）
   */
  rotateProvider(): IAdProvider | null {
    if (!this.currentProvider) {
      this.currentProvider = this.selectProvider();
      return this.currentProvider;
    }

    const currentIndex = this.providerOrder.indexOf(this.currentProvider.providerId);
    if (currentIndex === -1) {
      this.currentProvider = this.selectProvider();
      return this.currentProvider;
    }

    // 從下一個開始查找可用的提供商
    for (let i = 1; i < this.providerOrder.length; i++) {
      const nextIndex = (currentIndex + i) % this.providerOrder.length;
      const nextType = this.providerOrder[nextIndex];
      const provider = this.providers.get(nextType);

      if (provider && provider.getStatus() === 'ready') {
        this.currentProvider = provider;
        return provider;
      }
    }

    // 如果沒有其他可用提供商，保持當前
    return this.currentProvider;
  }

  /**
   * 獲取當前廣告商
   */
  getCurrentProvider(): IAdProvider | null {
    return this.currentProvider || this.selectProvider();
  }

  /**
   * 設置當前廣告商
   */
  setProvider(providerId: AdProviderId): void {
    const provider = this.providers.get(providerId);
    if (provider) {
      this.currentProvider = provider;
    }
  }

  /**
   * 註冊廣告完成事件處理函數
   */
  onCompleted(handler: AdCompletedHandler): () => void {
    this.completedHandlers.push(handler);

    // 返回取消註冊的函數
    return () => {
      const index = this.completedHandlers.indexOf(handler);
      if (index > -1) {
        this.completedHandlers.splice(index, 1);
      }
    };
  }

  /**
   * 觸發廣告完成事件
   */
  private async triggerCompletedEvent(provider: IAdProvider, data: any): Promise<void> {
    const eventData: AdCompletedEventData = {
      provider: provider.providerId,
      data,
      timestamp: Date.now(),
    };

    // 調用所有註冊的 handler
    const handlerPromises = this.completedHandlers.map(async (handler) => {
      try {
        await handler(eventData);
      } catch (error) {
        console.error(`[AdService] Error in completed handler:`, error);
      }
    });

    await Promise.all(handlerPromises);
  }

  /**
   * 打開廣告
   * @param providerId 可選，指定要使用的廣告商。如果不指定，則自動選擇可用的提供商
   */
  async open(providerId?: AdProviderId): Promise<any> {
    let provider: IAdProvider | null;

    if (providerId) {
      // 如果指定了廣告商，使用指定的
      provider = this.providers.get(providerId) || null;
      if (!provider) {
        throw new Error(`Ad provider "${providerId}" is not registered`);
      }
      if (provider.getStatus() !== 'ready') {
        throw new Error(`Ad provider "${providerId}" is not ready (status: ${provider.getStatus()})`);
      }
    } else {
      // 如果沒有指定，自動選擇可用的提供商
      provider = this.getCurrentProvider();
      if (!provider) {
        throw new Error('No available ad provider');
      }
    }

    try {
      console.log(`[AdService] Opening ad with provider: ${provider.providerId}`);
      const result = await provider.open();

      // 觸發廣告完成事件
      await this.triggerCompletedEvent(provider, result);

      // 如果沒有指定提供商，成功後輪播到下一個提供商
      if (!providerId) {
        this.rotateProvider();
      }

      return result;
    } catch (error) {
      console.error(`[AdService] Ad failed with provider ${provider.providerId}:`, error);

      // 如果沒有指定提供商且當前提供商失敗，嘗試下一個
      if (!providerId) {
        const nextProvider = this.rotateProvider();
        if (nextProvider && nextProvider !== provider) {
          console.log(`[AdService] Retrying with ${nextProvider.providerId}`);
          return nextProvider.open();
        }
      }
      throw error;
    }
  }

  /**
   * 獲取當前狀態
   */
  getStatus(): AdStatus {
    const provider = this.getCurrentProvider();
    return provider ? provider.getStatus() : 'idle';
  }

  /**
   * 獲取所有提供商的狀態
   */
  getAllStatuses(): Record<AdProviderId, AdStatus> {
    const statuses: Partial<Record<AdProviderId, AdStatus>> = {};

    for (const type of this.providerOrder) {
      const provider = this.providers.get(type);
      statuses[type] = provider ? provider.getStatus() : 'idle';
    }

    return statuses as Record<AdProviderId, AdStatus>;
  }
}

// 單例實例
let adServiceInstance: AdService | null = null;

export function getAdService(): AdService {
  if (!adServiceInstance) {
    adServiceInstance = new AdService();
  }
  return adServiceInstance;
}
